<template>
	<div>
		<modal
			v-show="!isTopupModalVisible"
			size="medium"
			:hide-footer="true"
			@close="$emit('close')"
		>
			<template #body>
				<div>
					<!-- Balance Header Card -->
					<div class="card text-white mb-3 border-0 bg-info">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center mb-2">
								<small class="text-xs fw-bold">
									Saldo Saat Ini
								</small>
								<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.8;">
								<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path>
								<path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path>
								</svg>
							</div>
							<h1 class="h2 fw-bold mb-0">
								<b class="">
									{{ formatMoney(currentBalance) }}
								</b>
							</h1>
						</div>
					</div>

					<!-- Product Summary -->
					<div class="card border-0 mb-3">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center py-2 border-bottom">
								<small class="text-xs fw-bold mb-0">Total Pesanan</small>
								<small class="fw-bold mb-0 text-dark">{{ filteredPrivateOrders.length }} pesanan</small>
							</div>

							<!-- Cost Breakdown -->
							<div class="mt-3">
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Total Harga Pesanan</small>
									<small class="fw-semibold text-dark">{{ formatMoney(subtotalAmount) }}</small>
								</div>
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Total Handling Fee</small>
									<small class="fw-semibold text-dark">{{ formatMoney(totalHandlingFee) }}</small>
								</div>
								<div class="d-flex justify-content-between mb-3">
									<small class="text-secondary">Biaya Layanan</small>
									<small class="fw-semibold text-dark">{{ formatMoney(platformFee) }}</small>
								</div>

								<!-- Total -->
								<div class="d-flex justify-content-between align-items-center pt-3 mt-3 border-top">
									<div>
										<span class="d-block text-xs text-uppercase fw-bold mb-1">Total Tagihan</span>
									</div>
									<div class="d-flex align-items-center">
										<div class="mr-2">
											<div v-if="isBalanceSufficient" class="sufficient-balance">
												Saldo Cukup
											</div>
											<div v-else class="insufficient-balance">
												Saldo tidak mencukupi
											</div>
										</div>
										<div class="fw-bold text-dark">
											<b>
												{{ formatMoney(totalAmount) }}
											</b>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Action Buttons -->
					<div class="row g-3 mt-4">
						<!-- Close/Cancel Button -->
						<div class="col-sm-2">
							<button
								class="btn btn-default w-100 border py-3"
								style="border: none;"
								:disabled="isSubmitting"
								@click="$emit('close')"
							>
								<i class="material-icons">close</i>
							</button>
						</div>
						<!-- Topup Button (shown when balance insufficient) -->
						<div class="col-sm-10 pl-0" v-if="!isBalanceSufficient">
							<button
								class="btn btn-primary py-3 fw-bold w-100"
								@click="handleTopup"
							>
								Topup
							</button>
						</div>

						<!-- Confirm Button (shown when balance sufficient) -->
						<div class="col-sm-10 pl-0" v-else>
							<button
								class="btn btn-primary py-3 fw-bold w-100"
								:class="{ 'is-loading': isSubmitting }"
								:disabled="isBtnDisabled || isSubmitting"
								@click="submit"
							>
								Proses
							</button>
						</div>
					</div>
				</div>
			</template>
		</modal>

		<!-- Topup Modal -->
		<topup-modal
			:is-visible="isTopupModalVisible"
			:initial-amount="topupAmount"
			@close="isTopupModalVisible = false"
			@success="handleTopupSuccess"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@/components/Modal.vue';
import TopupModal from '@/components/TopupModal.vue';
import { payWithBalance } from '@/api/private-order';
import Wallet from '@/api/wallet';
import { formatMoney, getAxiosErrorMessage } from '@/lib/helper';
import { MINIMUM_TOPUP } from '@/lib/statics';

export default {
	name: 'ModalProcessOrder',
	components: {
		Modal,
		TopupModal,
	},
	data() {
		return {
			filteredPrivateOrders: [],
			isSubmitting: false,
			currentBalance: 0,
			isTopupModalVisible: false,
		};
	},
	computed: {
		...mapGetters({
			privateOrders: 'monitoringPrivateOrder/privateOrders',
			selectedData: 'monitoringPrivateOrder/selectedData',
		}),
		subtotalAmount() {
			return this.filteredPrivateOrders.reduce((sum, order) => sum + (Number(order.total_product_price) || 0), 0);
		},
		totalHandlingFee() {
			return this.filteredPrivateOrders.reduce((sum, order) => sum + (Number(order.handling_fee) || 0), 0);
		},
		platformFee() {
			return this.filteredPrivateOrders.reduce((sum, order) => sum + (Number(order.service_fee) || 0), 0);
		},
		totalAmount() {
			return this.subtotalAmount + this.totalHandlingFee + this.platformFee;
		},
		isBalanceSufficient() {
			return this.currentBalance >= this.totalAmount;
		},
		balanceDeficit() {
			return this.totalAmount - this.currentBalance;
		},
		topupAmount() {
			return Math.max(this.balanceDeficit, MINIMUM_TOPUP);
		},
		isBtnDisabled() {
			return !this.filteredPrivateOrders.length || !this.totalAmount || !this.isBalanceSufficient;
		},
	},
	created() {
		this.init();
	},
	methods: {
		formatMoney,
		init() {
			const selectedData = this.selectedData || [];
			const privateOrders = this.privateOrders || [];
			this.filteredPrivateOrders = privateOrders.filter(order => selectedData.some(data => parseInt(data) === parseInt(order.order_id)) && (order.payment_status || order.payment?.status) === 'unpaid');
			this.fetchBalance();
		},
		fetchBalance() {
			Wallet.getBalance(
				(response) => {
					const data = response?.data || response || {};
					this.currentBalance = data.balance || 0;
				},
				(error) => {
					console.error('Failed to fetch balance:', error);
					this.currentBalance = 0;
				},
			);
		},
		handleTopup() {
			this.isTopupModalVisible = true;
		},
		handleTopupSuccess() {
			this.fetchBalance();
		},
		submit() {
			this.isSubmitting = true;
			const orderIds = this.filteredPrivateOrders.map(order => order.order_id);
			payWithBalance(
				{ order_ids: orderIds },
				() => {
					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Pembayaran Berhasil',
						text: `Pesanan berhasil diproses (${orderIds.length} pesanan)`,
					});
					this.$emit('success');
					this.$emit('close');
					this.isSubmitting = false;
				},
				(e) => {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Pembayaran Gagal',
						text: getAxiosErrorMessage(e),
					});
					this.isSubmitting = false;
				},
			);
		},
	},
};
</script>
<style scoped>
.sufficient-balance {
	background-color: #d4edda;
	color: #155724;
	padding: 5px 10px;
	border-radius: 5px;
	font-weight: bold;
	font-size: 11px;
}

.insufficient-balance {
	background-color: #ff3647;
	color: #fff;
	padding: 5px 10px;
	border-radius: 5px;
	font-weight: bold;
	font-size: 11px;
}
</style>
